/**
 * Unified Analytics System for Merchant Portal
 * Supports both Google Analytics (existing) and Internal Event Tracking (new)
 */

import ReactGA from 'react-ga';
import {
  trackWebClick as trackInternalWebClick,
  trackWebViewScreen,
  getInternalAnalyticsConfig,
  setAnalyticsUser,
} from './internalAnalytics';

// Initialize Google Analytics (existing logic)
const gaId = process.env.REACT_APP_GA_ID;
const gaEnabled = !!gaId;

// Only initialize GA in browser environment (not in tests)
if (gaEnabled && typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
  ReactGA.initialize(gaId);
  console.log('[Analytics] Google Analytics initialized successfully');
} else if (process.env.NODE_ENV !== 'test') {
  console.log('[Analytics] Google Analytics disabled - no GA ID configured');
}

/**
 * Track events across both GA and internal systems
 * @param {Object} eventData - Event data in GA format
 */
export const trackEvent = async (eventData) => {
  const { category, action, label, value, ...additionalProperties } = eventData;

  // Track with Google Analytics (existing)
  if (gaEnabled && typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
    try {
      ReactGA.event({
        category,
        action,
        label,
        value,
      });
    } catch (error) {
      console.error('[Analytics] Google Analytics event error:', error);
    }
  }

  // Track with Internal System (new)
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
    try {
      await trackInternalWebClick({
        category,
        action,
        label,
        value,
        additionalProperties,
      });
    } catch (error) {
      console.error('[Analytics] Internal Analytics event error:', error);
    }
  }

  // Development logging
  if (process.env.NODE_ENV !== 'production') {
    console.log('[Analytics] Event tracked:', { category, action, label, value });
  }
};

/**
 * Track page views across both GA and internal systems
 * @param {string} path - Page path
 */
export const trackPageView = async (path) => {
  // Track with Google Analytics (existing)
  if (gaEnabled && typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
    try {
      ReactGA.set({ page: path });
      ReactGA.pageview(path);
    } catch (error) {
      console.error('[Analytics] Google Analytics pageview error:', error);
    }
  }

  // Track with Internal System (new)
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
    try {
      await trackWebViewScreen({
        // eslint-disable-next-line no-undef
        screen_url: window.location.href,
      });
    } catch (error) {
      console.error('[Analytics] Internal Analytics pageview error:', error);
    }
  }

  // Development logging
  if (process.env.NODE_ENV !== 'production') {
    console.log('[Analytics] Page view tracked:', path);
  }
};

/**
 * Track timing events across both GA and internal systems
 * @param {Object} timingData - Timing data
 */
export const trackTiming = async (timingData) => {
  const { category, variable, value, label } = timingData;

  // Track with Google Analytics (existing)
  if (gaEnabled && typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
    try {
      ReactGA.timing({
        category,
        variable,
        value,
        label,
      });
    } catch (error) {
      console.error('[Analytics] Google Analytics timing error:', error);
    }
  }

  // Track with Internal System (new)
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
    try {
      await trackInternalWebClick({
        category: 'timing',
        action: 'performance_measurement',
        label: `${category}_${variable}`,
        additionalProperties: {
          timing_category: category,
          timing_variable: variable,
          timing_value_ms: value,
          original_label: label,
        },
      });
    } catch (error) {
      console.error('[Analytics] Internal Analytics timing error:', error);
    }
  }

  // Development logging
  if (process.env.NODE_ENV !== 'production') {
    console.log('[Analytics] Timing tracked:', { category, variable, value, label });
  }
};

/**
 * Set user properties (GA only for now)
 * @param {Object} properties - User properties
 */
export const setUserProperties = (properties) => {
  if (gaEnabled && typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
    try {
      Object.entries(properties).forEach(([key, value]) => {
        ReactGA.set({ [key]: value });
      });
    } catch (error) {
      console.error('[Analytics] Google Analytics set properties error:', error);
    }
  }

  // Development logging
  if (process.env.NODE_ENV !== 'production') {
    console.log('[Analytics] User properties set:', properties);
  }
};

/**
 * Get analytics system status for debugging
 */
export const getAnalyticsStatus = () => {
  return {
    googleAnalytics: {
      enabled: gaEnabled,
      gaId: gaId,
    },
    internalAnalytics: getInternalAnalyticsConfig(),
  };
};

// Export individual system status for debugging
export const isGAEnabled = () => gaEnabled;
export const isInternalAnalyticsEnabled = () => {
  const config = getInternalAnalyticsConfig();
  return config.enabled;
};

// Export user management for analytics
export const setUser = setAnalyticsUser;
